/**
 * @fileoverview Main App Component
 * @description Root application component with routing configuration and
 * authentication provider setup. Includes role-based access control for
 * production dashboard and administrative features.
 * @author Production Dashboard Team
 * @since 1.0.0
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './components/auth/LoginPage';
import Dashboard from './components/dashboard/Dashboard';
import FinancialAnalysisPage from './components/pages/FinancialAnalysisPage';
import ScenarioPlannerWithRevenue from "./components/pages/ScenarioPlannerWithRevenue";
import UserManagementPage from './components/pages/UserManagementPage';
import ProfilePage from './components/pages/ProfilePage';
import SettingsPage from './components/pages/SettingsPage';
import NotFoundPage from './components/pages/NotFoundPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import './App.css';
import ProductionDashboard from "./components/dashboard/production/ProductionDashboard";
import InventoryManager from "./components/pages/InventoryManager";
import ProductionPlansPage from "./components/pages/ProductionPlansPage";
import AIChatAnalyticsPage from "./components/pages/AIChatAnalyticsPage";
import QuickBooksIntegrationPage from "./components/pages/QuickBooksIntegrationPage";
import { PAGE_ROLES, DEFAULT_LANDING_ROUTE } from './config/roleAccess';

// Overview isn't reachable by every role (STAFF can't see it), so the
// post-login landing page has to depend on who's actually logged in rather
// than being a single hardcoded redirect.
function DefaultLandingRedirect() {
  const { userType } = useAuth();
  const target = DEFAULT_LANDING_ROUTE[userType] || '/dashboard';
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <div className="App">
      <ToastProvider>
        <Router>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

                {/* Protected routes with layout */}
              <Route 
                  path="/*"
                element={
                  <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
                >
                  {/* Dashboard routes */}
                  <Route path="dashboard" element={
                      <ProtectedRoute requiredRole={PAGE_ROLES.dashboard}>
                        <Dashboard />
                      </ProtectedRoute>
                  } />

                  {/* Production Dashboard - Manager/Admin only */}
                  <Route path="production" element={
                      <ProtectedRoute requiredRole={PAGE_ROLES.productDashboard}>
                        <ProductionDashboard />
                      </ProtectedRoute>
                  }
              />
                  <Route path="inventory-overview" element={
                      <ProtectedRoute requiredRole={PAGE_ROLES.inventoryManager}>
                        <InventoryManager />
                      </ProtectedRoute>
                  } />
                  <Route path="financial-analysis" element={
                      <ProtectedRoute requiredRole={PAGE_ROLES.financialAnalysis}>
                        <FinancialAnalysisPage />
                      </ProtectedRoute>
                  } />
                  <Route path="scenario-planning" element={
                      <ProtectedRoute requiredRole={PAGE_ROLES.scenarioPlanning}>
                        <ScenarioPlannerWithRevenue />
                      </ProtectedRoute>
                  } />
                  <Route path="production-plans" element={
                      <ProtectedRoute requiredRole={PAGE_ROLES.productionPlans}>
                        <ProductionPlansPage />
                      </ProtectedRoute>
                  } />

                  {/* Admin routes */}
                  <Route path="admin/users"
                element={
                  <ProtectedRoute requiredRole="SUPER_ADMIN">
                    <UserManagementPage />
                  </ProtectedRoute>
                } 
              />

                  {/* User Profile Page */}
              <Route
                path="profile"
                element={<ProfilePage />}
              />
                  <Route
                    path="settings"
                    element={<SettingsPage />}
                  />
                  <Route
                    path="settings/ai-analytics"
                    element={<AIChatAnalyticsPage />}
                  />
                  <Route
                    path="settings/integrations"
                    element={
                      <ProtectedRoute requiredRole={['SUPER_ADMIN', 'ADMIN']}>
                        <QuickBooksIntegrationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="documentation"
                element={
                  <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
                    <h2>Documentation</h2>
                    <p>This is a placeholder for the Documentation page.</p>
                  </div>
                } 
              />

              {/* Root redirect — lands on a page the user's role can actually see */}
              <Route index element={<DefaultLandingRedirect />} />

              {/* 404 page */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

                {/* Static pages */}
              <Route
                  path="/forgot-password"
                element={
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h2>Forgot Password</h2>
                  <p>This feature will be implemented in a future update.</p>
                  <a href="/login">Back to Login</a>
                </div>
                } 
              />
              <Route 
                  path="/terms"
                element={
                <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
                  <h2>Terms of Service</h2>
                  <p>This is a placeholder for the Terms of Service page.</p>
                  <a href="/login">Back to Login</a>
                </div>
                } 
              />
              <Route 
                  path="/privacy"
                element={
                <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
                  <h2>Privacy Policy</h2>
                  <p>This is a placeholder for the Privacy Policy page.</p>
                  <a href="/login">Back to Login</a>
                </div>
                } 
              />
            </Routes>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </div>
  );
}

export default App;
